import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { openBundleByUrl } from '~/utils/category';
import classNames from 'classnames';
import styles from './Bundle.scss';
import BundleAmount from '~/components/BundleAmount/BundleAmount';
import PriceWrapper from '~/components/PriceWrapper/PriceWrapper';
import PurchaseButton from '~/components/Button/PurchaseButton';
import { isInGameBrowser, isMobileOrTabletWindow, redirectToLogin } from '~/utils/utils';
import { Howl } from 'howler';
import useResourceLoader from '~/hooks/useResourceLoader';
import { getSupportedVideo } from '~/utils/video';
import { groupBundlesByName } from '~/utils/bundles';
import RealPurchaseButton from '~/components/Button/RealPurchaseButton';
import { isChina, isSafariBrowser } from '~/utils/settings';
import { interpolate, t } from '~/utils/localization';
import { DivTooltip, Interpolate } from '@wg/wows-react-uikit';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import Attention from '~/components/Attention/Attention';
import { RootState, useAppSelector } from '~/Store';
import { ButtonVariant } from '~/components/Button/Button';
import { BUNDLE_DECORATION, BundlePurchaseTypes } from '~/types/bundle';
import { items, assets } from '@wg/wows-entities/const';
import Currency from '~/components/Currency/Currency';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import Timer, { TimerVariant } from '~/components/Timer/Timer';
import CountDown from '~/components/CountDown/CountDown';

interface IBundleProps {
    groupName: string;
    groups: ReturnType<typeof groupBundlesByName>;
    isLastGroup: boolean;
    isDisabledAnimation?: boolean;
    isSoundAvailable: boolean;
    isShowcaseForRealMoney: boolean;
    isHiddenProductsInfo: boolean;
    isLonelyBundle?: boolean;
    isHalfBundle?: boolean;
}

const stateSelector = (state: RootState) => {
    return {
        currentPage: state.app.currentPage,
        groupBundles: state.app.groupBundles,
        categoriesAnimationStatuses: state.app.categoriesAnimationStatuses,
        clientSettings: state.account.clientSettings,
        categories: state.app.categories,
        bundles: state.app.bundles,
    };
};

interface VideoElement {
    src: string;
    className?: string;
    autoPlay?: boolean;
}

const assetsArray = Object.values(assets);

const VideoElement = React.memo(
    React.forwardRef(({ className, src, autoPlay }: VideoElement, ref: React.RefObject<HTMLVideoElement>) => {
        return <video className={className} preload={'auto'} autoPlay={autoPlay} muted loop src={src} ref={ref} />;
    }),
);

const Bundle = React.forwardRef((props: IBundleProps, ref: React.RefObject<HTMLDivElement>) => {
    const state = useAppSelector(stateSelector, shallowEqual);
    const selectedBundleStore = state.groupBundles || {};
    const currentGroupsBundles: IBundle[] = props.groups[props.groupName];
    const isCn = isChina();
    const idleAnimationRef = React.useRef<HTMLVideoElement>(null);
    const hoverAnimationRef = React.useRef<HTMLVideoElement>(null);

    const selectedBundleIndex = React.useMemo(() => {
        return currentGroupsBundles?.reduce((state: number, bundle: IBundle, index: number) => {
            if (bundle.id === selectedBundleStore?.[props.groupName]) {
                state = index;
            }
            return state;
        }, null);
    }, [props.groupName]);

    let currentBundle: IBundle;

    if (selectedBundleIndex !== null && selectedBundleIndex !== undefined) {
        currentBundle = currentGroupsBundles[selectedBundleIndex];
    } else {
        currentBundle = currentGroupsBundles.at(-1);
    }

    const [selectedBundle, updateSelectedBundle] = React.useState(currentBundle);

    const howl = React.useMemo<Nullable<Howl>>(() => {
        if (selectedBundle.audioHover?.mp3) {
            return new Howl({
                src: selectedBundle.audioHover.mp3,
            });
        }
        return null;
    }, [selectedBundle]);

    const idleAnimation = React.useMemo(() => {
        return getSupportedVideo(selectedBundle.bundleCardIdleVideo);
    }, [selectedBundle]);
    const hoverAnimation = React.useMemo(() => {
        return getSupportedVideo(selectedBundle.bundleCardHoverVideo);
    }, [selectedBundle]);

    const [isLoadingResources, resourcesMap] = useResourceLoader([idleAnimation, hoverAnimation], isInGameBrowser, isSafariBrowser, [idleAnimation, hoverAnimation]);

    React.useEffect(() => {
        return () => {
            howl?.stop();
        };
    }, []);

    React.useEffect(() => {
        if (selectedBundle.purchaseType === BundlePurchaseTypes.REAL_CURRENCY && state.bundles[selectedBundle.id]) {
            updateSelectedBundle(state.bundles[selectedBundle.id]);
        }
    }, [state.bundles]);

    const bonusItem = isCn
        ? selectedBundle.entitlements.find((item) => assetsArray.includes(item.type as any))
        : selectedBundle.entitlements.find((item) => item.isBonus && assetsArray.includes(item.type as any));

    const goToBundle = () => {
        if (props.isHiddenProductsInfo) {
            return redirectToLogin();
        }
        openBundleByUrl(state.currentPage?.name, selectedBundle.id);
    };

    const classes = classNames(
        styles.bundle,
        {
            [styles.half]: props.isHalfBundle || currentBundle.sizeInGrid === '1/2' || props.isLonelyBundle,
            [styles.lonelyBundle]: props.isLonelyBundle,
        },
        props.groupName,
    );

    const bundle = { ...selectedBundle, quantityData: {} };

    const classesIdleVideo = classNames(styles.video, styles.animationIdle, {
        [styles.visible]: !props.isDisabledAnimation && isLoadingResources,
    });

    const classesHoverVideo = classNames(styles.video, styles.animationHover);

    const classesImage = classNames(styles.image, {
        [styles.hidden]: !props.isDisabledAnimation && isLoadingResources,
    });

    const classesHoverImage = classNames(styles.image, styles.hover, {
        [styles.hidden]: !props.isDisabledAnimation && isLoadingResources,
    });

    const classesTitle = classNames(styles.title, {
        [styles.kalam]: selectedBundle?.decoration.includes(BUNDLE_DECORATION.KALAM_FONTS),
    });

    const startAnimation = () => {
        const hover = hoverAnimationRef.current;
        hoverAnimationRef.current.play();
        hover.classList.add(styles.visibleHoverAnimation);
        idleAnimationRef.current.classList.remove(styles.visible);
    };

    const stopAnimation = () => {
        const hover = hoverAnimationRef.current;
        hoverAnimationRef.current.pause();
        hoverAnimationRef.current.currentTime = 0;
        hover.classList.remove(styles.visibleHoverAnimation);
        idleAnimationRef.current.classList.add(styles.visible);
    };

    const onMouseEnter = () => {
        if (props.isSoundAvailable && howl && !howl.playing()) {
            howl.volume(1);
            howl.play();
        }
        !props.isDisabledAnimation && !isMobileOrTabletWindow && isLoadingResources && startAnimation();
    };

    const onMouseLeave = () => {
        !props.isDisabledAnimation && !isMobileOrTabletWindow && isLoadingResources && stopAnimation();
    };

    const hasAmounts = currentGroupsBundles.length > 1;
    const classesPriceWrapper = classNames(styles.priceWrapper, {
        [styles.withoutAmount]: !hasAmounts,
    });

    return (
        <div className={classes} onClick={goToBundle} ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <div className={styles.imageContainer}>
                <img className={classesImage} src={selectedBundle.icons.groupingIdleImage} alt={bundle.title} />
                <img className={classesHoverImage} src={selectedBundle.icons.groupingHoverImage} alt={bundle.title} />
                {!isMobileOrTabletWindow && resourcesMap[idleAnimation] && resourcesMap[hoverAnimation] && (
                    <>
                        <VideoElement className={classesIdleVideo} autoPlay={true} src={resourcesMap[idleAnimation]} ref={idleAnimationRef} />
                        <VideoElement className={classesHoverVideo} autoPlay={false} src={resourcesMap[hoverAnimation]} ref={hoverAnimationRef} />
                    </>
                )}
                {!isCn && bonusItem?.amount > 0 && (
                    <div className={styles.bonusWrapper}>
                        <DivTooltip
                            className={styles.bonus}
                            tooltipBody={
                                <DefaultTooltip
                                    text={
                                        <Interpolate
                                            str={t('Purchase this bundle and get %(component)s as a bonus')}
                                            component={<Currency currency={bonusItem.type} amount={bonusItem.amount} withoutTooltip={true} />}
                                        />
                                    }
                                />
                            }
                        >
                            <span className={styles.bonusTitle}>
                                <Interpolate str={t('+%(amount)s')} amount={<Currency currency={bonusItem.type} amount={bonusItem.amount} withoutTooltip={true} />} />
                            </span>
                        </DivTooltip>
                    </div>
                )}
                {isCn && (
                    <div className={styles.bonusWrapper}>
                        {bonusItem?.amount > 0 && (
                            <div className={classNames(styles.bonus, styles.chineseBonusTransform)}>
                                <span className={styles.bonusTitle}>
                                    <Interpolate str={t('&times;%(amount)s')} amount={<Currency currency={bonusItem.type} amount={bonusItem.amount} withoutTooltip={true} />} />
                                </span>
                            </div>
                        )}
                        {selectedBundle.primaryItem?.type === items.LOOTBOX && props.isShowcaseForRealMoney && (
                            <DivTooltip
                                className={styles.chineseBonus}
                                tooltipBody={
                                    <DefaultTooltip
                                        text={
                                            <Interpolate
                                                str={t('Purchase this bundle and get %(component)s as a bonus')}
                                                component={<WoWSEntity type={selectedBundle.primaryItem.type} amount={selectedBundle.primaryItem.amount} id={selectedBundle.primaryItem.identifier} />}
                                            />
                                        }
                                    />
                                }
                            >
                                <span className={styles.chineseBonusTitle}>{t('Бонус')}</span>
                                <span className={styles.chineseBonusContent}>
                                    <span>{interpolate(t('+{amount}'), { amount: selectedBundle.primaryItem.amount })}</span>
                                    <span className={'we-inline we-nowrap we-lootbox__title with-icon'} />
                                </span>
                            </DivTooltip>
                        )}
                    </div>
                )}
            </div>
            {!props.isHiddenProductsInfo && (
                <div className={styles.infoContainer}>
                    <div className={styles.info}>
                        <div className={classesTitle}>{selectedBundle.title}</div>
                    </div>
                    {hasAmounts && (
                        <div className={styles.amount}>
                            <BundleAmount
                                bundles={currentGroupsBundles}
                                selectedBundleId={selectedBundle.id}
                                onChange={(bundle) => {
                                    updateSelectedBundle(bundle);
                                }}
                            />
                        </div>
                    )}
                    <div className={classesPriceWrapper}>
                        <PriceWrapper
                            bundle={bundle}
                            discountClassName={styles.discount}
                            withoutAnimation={false}
                            wrapperClassName={styles.pricesWrapper}
                            hiddenDiscount={true}
                            priceClassName={styles.price}
                        />
                    </div>
                    <div className={styles.purchaseWrapper}>
                        <div className={styles.purchase}>
                            {bundle.productCode ? (
                                <>
                                    {bundle.originalProduct?.isPurchasable ? (
                                        <RealPurchaseButton bundle={selectedBundle} buttonClassName={styles.button} />
                                    ) : isCn ? (
                                        <Attention label={t("You've exceeded your daily limit of purchase. Limit is being reset daily at 08:00.")} className={styles.limitAttention} />
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <PurchaseButton
                                        bundle={selectedBundle}
                                        buttonClassName={styles.button}
                                        shortageClassName={styles.shortageClassName}
                                        classNameFastGoldLink={styles.fastGoldWrapper}
                                        classNameChinaFastGoldLink={styles.fastGoldWrapper}
                                        buttonVariant={ButtonVariant.orange}
                                        isForceRenderPurchaseButton={true}
                                    />
                                </>
                            )}
                            {bundle.promoTimer?.showCountdown && bundle.decoration?.includes(BUNDLE_DECORATION.NY26_TIMER) && (
                                <div className={styles.timerLabel}>
                                    <Timer
                                        time={bundle.promoTimer.activeTill}
                                        className={styles.timer}
                                        timerVariant={TimerVariant.RED}
                                        tooltipBody={
                                            !!bundle.promoTimer.description ? (
                                                <DefaultTooltip
                                                    title={t('Предложение ограничено по времени')}
                                                    text={bundle.promoTimer.description}
                                                    footer={
                                                        <div className={styles.tooltipTimeWrapper}>
                                                            <div className={styles.tooltipTimerTitle}>
                                                                <span>{t('Available for:')}</span>
                                                                <CountDown date={bundle.promoTimer.activeTill} className={styles.tooltipTimerLabel} withoutIcon={true} />
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            ) : null
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default Bundle;
